/**
 * Cross-game achievement layer.
 *
 * Operates on the persistent profile (src/site/profile.ts) rather than
 * individual game events. Achievements are checked after every recorded
 * run; newly-unlocked ones fire a toast.
 *
 * Storage: aether.achievements_xgame in localStorage. The legacy
 * Drift-only achievement system lives in src/ui/achievements.ts and is
 * untouched — these run in parallel.
 */

import { getProfile, type Profile, rankForXp } from "./profile";

const KEY = "aether.achievements_xgame";

export interface XGameAchievement {
  id: string;
  title: string;
  detail: string;
  /** Predicate over the profile + the run that just ended. Return true if earned. */
  earned: (p: Profile) => boolean;
}

const ALL_GAME_IDS: readonly string[] = [
  "drift", "pulse", "bulwark", "echo", "spire", "quickdraw",
  "foraging", "ascent", "lattice", "sigil", "resonance",
  "frequency", "hailstorm",
];

export const XGAME_ACHIEVEMENTS: XGameAchievement[] = [
  {
    id: "first_step",
    title: "First Step",
    detail: "Finished your first run on any game.",
    earned: (p) => Object.keys(p.gameRuns).length >= 1,
  },
  {
    id: "explorer",
    title: "Explorer",
    detail: "Played 5 different games.",
    earned: (p) => Object.keys(p.gameRuns).length >= 5,
  },
  {
    id: "completionist",
    title: "Completionist",
    detail: "Played all 13 games at least once.",
    earned: (p) => ALL_GAME_IDS.every((g) => (p.gameRuns[g] ?? 0) > 0),
  },
  {
    id: "rank_adept",
    title: "Adept",
    detail: "Reached the Adept rank (500 XP).",
    earned: (p) => p.totalXp >= 500,
  },
  {
    id: "rank_skilled",
    title: "Skilled",
    detail: "Reached the Skilled rank (2,000 XP).",
    earned: (p) => p.totalXp >= 2000,
  },
  {
    id: "rank_expert",
    title: "Expert",
    detail: "Reached the Expert rank (6,000 XP).",
    earned: (p) => p.totalXp >= 6000,
  },
  {
    id: "rank_master",
    title: "Master",
    detail: "Reached the Master rank (15,000 XP).",
    earned: (p) => p.totalXp >= 15_000,
  },
  {
    id: "rank_grandmaster",
    title: "Grandmaster",
    detail: "Reached the Grandmaster rank (40,000 XP).",
    earned: (p) => p.totalXp >= 40_000,
  },
  {
    id: "daily_streak_3",
    title: "Three-Day Streak",
    detail: "Played on 3 different days.",
    earned: (p) => p.daysPlayed.length >= 3,
  },
  {
    id: "daily_streak_7",
    title: "Week-Long Streak",
    detail: "Played on 7 different days.",
    earned: (p) => p.daysPlayed.length >= 7,
  },
  {
    id: "centurion",
    title: "Centurion",
    detail: "Recorded 100 total runs across all games.",
    earned: (p) => Object.values(p.gameRuns).reduce((a, b) => a + b, 0) >= 100,
  },
];

function loadEarned(): Record<string, true> {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveEarned(m: Record<string, true>) {
  try { localStorage.setItem(KEY, JSON.stringify(m)); }
  catch { /* private mode */ }
}

/** Returns the list of achievements newly earned by this run. */
export function checkAchievements(): XGameAchievement[] {
  const p = getProfile();
  const earned = loadEarned();
  const fresh: XGameAchievement[] = [];
  for (const a of XGAME_ACHIEVEMENTS) {
    if (earned[a.id]) continue;
    if (a.earned(p)) {
      earned[a.id] = true;
      fresh.push(a);
    }
  }
  if (fresh.length > 0) saveEarned(earned);
  return fresh;
}

export function getEarnedAchievements(): string[] {
  return Object.keys(loadEarned());
}

/** Toast for a single newly-earned achievement. Stacks multiple. */
export function showAchievementToast(a: XGameAchievement, offset = 0) {
  const el = document.createElement("div");
  el.className = "achievement-toast";
  el.setAttribute("role", "status");
  el.setAttribute("aria-live", "polite");
  el.style.bottom = `${88 + offset * 80}px`;
  el.innerHTML = `<div class="ach-banner">ACHIEVEMENT UNLOCKED</div>
    <div class="ach-title">${a.title}</div>
    <div class="ach-detail">${a.detail}</div>`;
  document.body.appendChild(el);
  requestAnimationFrame(() => el.classList.add("in"));
  window.setTimeout(() => {
    el.classList.remove("in");
    el.classList.add("out");
    window.setTimeout(() => el.remove(), 400);
  }, 3600);
}

// Convenience helper to keep callers thin.
export function _checkAndShowToasts(): void {
  const fresh = checkAchievements();
  fresh.forEach((a, i) => showAchievementToast(a, i));
}

/** Helper for any UI surface that wants to render the achievement grid. */
export function getRankAndCounts() {
  const p = getProfile();
  return {
    rank: rankForXp(p.totalXp).name,
    earned: Object.keys(loadEarned()).length,
    total: XGAME_ACHIEVEMENTS.length,
  };
}
